<template>
  <div 
    v-click-outside="closeFloatingPanel"
    class="tilt-picker" 
  >
    <button
      ref="trigger"
      class="tilt-trigger"
      :class="{ 'is-open': isOpen, 'has-tilt': tilt !== 0 }"
      :title="`Tilt: ${displayTilt} deg`"
      @click="toggleFloatingPanel"
    >
      <i class="bi bi-arrow-clockwise" />
      <span 
        v-if="tilt !== 0"
        class="tilt-trigger__value"
      >
        {{ displayTilt }} °
      </span>
    </button>
 
    <transition name="tilt-panel">
      <div
        v-if="isOpen"
        ref="panel"
        class="tilt-panel"
        :style="floatingPanelStyle"
        role="dialog"
        aria-label="Tilt control"
      >
        <div class="tilt-panel__head">
          <span class="tilt-panel__title">Tilt</span>
          <button
            class="tilt-panel__reset"
            :disabled="tilt === 0"
            :class="{ 'is-active': tilt !== 0 }"
            @click="reset"
          >
            Reset
          </button>
        </div>
 
        <div class="tilt-presets">
          <button
            v-for="preset in presets"
            :key="preset"
            class="tilt-preset"
            :class="{ 'is-selected': tilt === preset }"
            @click="selectPreset(preset)"
          >
            {{ preset > 0 ? `+${preset}` : preset }}
          </button>
        </div>
 
        <div class="tilt-slider-wrap">
          <input
            type="range"
            class="tilt-slider"
            :min="min"
            :max="max"
            :step="step"
            :value="tilt"
            @input="onSlider"
          >
          <div
            class="tilt-slider-fill"
            :style="fillStyle"
          />
          <span class="tilt-grade ml-2">{{ displayTilt > 0 ? `+${displayTilt}` : displayTilt }} deg</span>
        </div>

        <button
          type="button"
          class="tilt-panel__apply-all"
          @click="$emit('apply-all', tilt)"
        >
          Apply to all shelves
        </button>
      </div>
    </transition>
  </div>
</template>
 
<script>
import fixedDropdownPosition from '@frontend/mixins/fixedDropdownPosition';

export default {
  name: 'TiltPicker',
  directives: {
    clickOutside: {
      bind(el, binding) {
        el._handler = (e) => { if (!el.contains(e.target)) binding.value(e); };
        document.addEventListener('mousedown', el._handler);
      },
      unbind(el) {
        document.removeEventListener('mousedown', el._handler);
      },
    },
  },

  mixins: [fixedDropdownPosition],

  floatingPanel: {
    gap: 7,
    viewportPadding: 8,
    rightOffset: 0,
  },
 
  model: { prop: 'value', event: 'change' },

  props: {
    value: { type: Number, default: 0 },
    min:   { type: Number, default: -45 },
    max:   { type: Number, default: 45 },
    step:  { type: Number, default: 1 },
  },
 
  data() {
    return {
      isOpen: false,
      tilt: this.value,
      presets: [-45, -30, -15, 0, +15, +30, +45],
    };
  },

  computed: {
    displayTilt() {
      const tilt = Number(this.tilt);

      if (!Number.isFinite(tilt)) {
        return 0;
      }

      const stepText = String(this.step);
      const decimalIndex = stepText.indexOf('.');
      const precision = decimalIndex === -1 ? 0 : stepText.length - decimalIndex - 1;

      return Number(tilt.toFixed(precision));
    },
    fillStyle() {
      const range   = this.max - this.min;                      // 90
      const zeroPos = ((0 - this.min) / range) * 100;           // 50%
      const valPos  = ((this.tilt - this.min) / range) * 100;   // 0-100%
 
      const left  = Math.min(zeroPos, valPos);
      const width = Math.abs(valPos - zeroPos);
 
      return { left: `${left}%`, width: `${width}%` };
    },
  },

  watch: {
    value(v) { this.tilt = v; },
  },

  methods: {
    selectPreset(val) {
      this.tilt = val;
      this.$emit('change', val);
    },
 
    onSlider(e) {
      const val = Number(e.target.value);
      this.tilt = val;
      this.$emit('change', val);
    },
 
    reset() {
      this.tilt = 0;
      this.$emit('change', 0);
    },
  },
};
</script>
 
<style scoped>
.tilt-picker {
  --teal:       #2eb89a;
  --teal-dark:  #23997f;
  --teal-dim:   rgba(46, 184, 154, .13);
  --teal-ring:  rgba(46, 184, 154, .25);
  --neg:        #f97316;   /* warm orange for negative tilt */
  --pos:        #2eb89a;   /* teal for positive tilt */
  --border:     #e4e7eb;
  --bg:         #ffffff;
  --bg-soft:    #f7f8fa;
  --text:       #1a1d23;
  --muted:      #6b7280;
  --light:      #9ca3af;
  --radius:     10px;
  --radius-sm:  6px;
  --shadow:     0 8px 24px rgba(0,0,0,.11), 0 2px 6px rgba(0,0,0,.07), 0 0 0 1px rgba(0,0,0,.04);
 
  position: relative;
  display: inline-flex;
}
 
.tilt-trigger {
  border: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 9px;
  background: var(--bg);
  border-radius: var(--radius-sm);
  font-size: 12.5px;
  font-weight: 500;
  color: var(--muted);
  cursor: pointer;
  transition: background .14s, border-color .14s, color .14s, box-shadow .14s;
  line-height: 1;
}
 
.tilt-trigger:hover {
  background: var(--bg-soft);
  border-color: #c9cdd4;
  color: var(--text);
}
 
.tilt-trigger__icon {
  flex-shrink: 0;
  transition: transform .25s ease;
}
 
.tilt-trigger__value {
  font-variant-numeric: tabular-nums;
  min-width: 26px;
  text-align: center;
}
 
/* ── Panel ── */
.tilt-panel {
  position: fixed;
  width: 280px;
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow);
  padding: 14px 14px 12px;
  z-index: 1100;
  transform-origin: top right;
}
 
/* ── Panel header ── */
.tilt-panel__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
 
.tilt-panel__title {
  font-size: 13px;
  font-weight: 700;
  color: var(--text);
  letter-spacing: -.1px;
}
 
.tilt-panel__reset {
  font-size: 11.5px;
  font-weight: 500;
  color: var(--light);
  background: transparent;
  border: none;
  padding: 2px 6px;
  border-radius: 4px;
  cursor: pointer;
  transition: color .13s, background .13s;
}
 
.tilt-panel__reset.is-active {
  color: var(--teal);
  cursor: pointer;
}
 
.tilt-panel__reset.is-active:hover {
  background: var(--teal-dim);
}
 
/* ── Presets ── */
.tilt-presets {
  display: flex;
  gap: 5px;
  margin-bottom: 14px;
}
 
.tilt-preset {
  flex: 1;
  padding: 5px 0;
  font-size: 12px;
  font-weight: 600;
  color: var(--muted);
  background: var(--bg-soft);
  border: 1.5px solid var(--border);
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: background .12s, border-color .12s, color .12s, transform .1s;
  text-align: center;
  font-variant-numeric: tabular-nums;
}
 
.tilt-preset:hover {
  background: var(--teal-dim);
  border-color: var(--teal);
  color: var(--teal);
}
 
.tilt-preset.is-selected {
  background: var(--teal);
  border-color: var(--teal);
  color: #fff;
  transform: scale(1.05);
  box-shadow: 0 2px 6px rgba(46, 184, 154, .3);
}
 
/* ── Slider ── */
.tilt-slider-wrap {
  position: relative;
  height: 20px;
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}
 
/* Custom track via the range input */
.tilt-slider {
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 4px;
  border-radius: 99px;
  background: var(--border);
  outline: none;
  cursor: pointer;
  position: relative;
  z-index: 2;
}
 
/* Thumb */
.tilt-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--teal);
  border: 2px solid #fff;
  box-shadow: 0 0 0 1.5px var(--teal), 0 2px 4px rgba(0,0,0,.15);
  cursor: grab;
  transition: transform .12s, box-shadow .12s;
}
 
.tilt-slider::-webkit-slider-thumb:active {
  cursor: grabbing;
  transform: scale(1.15);
  box-shadow: 0 0 0 3px var(--teal-ring), 0 2px 4px rgba(0,0,0,.15);
}
 
.tilt-slider::-moz-range-thumb {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--teal);
  border: 2px solid #fff;
  box-shadow: 0 0 0 1.5px var(--teal);
  cursor: grab;
}
 
/* Centre-fill overlay */
.tilt-slider-fill {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  height: 4px;
  background: var(--teal);
  border-radius: 99px;
  pointer-events: none;
  z-index: 1;
  transition: left .08s, width .08s;
}
 
/* ── Readout ── */
.tilt-readout {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 8px;
  border-top: 1px solid var(--border);
  margin-top: 4px;
}
 
.tilt-readout__label {
  font-size: 11px;
  font-weight: 500;
  letter-spacing: .04em;
  text-transform: uppercase;
  color: var(--light);
}
 
.tilt-readout__value {
  font-size: 15px;
  font-weight: 700;
  color: var(--text);
  font-variant-numeric: tabular-nums;
  letter-spacing: -.3px;
  transition: color .15s;
}
 
.tilt-readout__value.neg { color: var(--neg); }
.tilt-readout__value.pos { color: var(--pos); }
 
/* ── Panel transition ── */
.tilt-panel-enter-active,
.tilt-panel-leave-active {
  transition: opacity .16s ease, transform .16s ease;
}
 
.tilt-panel-enter,
.tilt-panel-leave-to {
  opacity: 0;
  transform: scale(.96) translateY(-4px);
}
.tilt-grade {
  font-size: 10px;
}

.tilt-panel__apply-all {
  display: block;
  width: 100%;
  padding: 2px 0;
  border: 0;
  border-radius: 6px;
  color: var(--text);
  background: transparent;
  font-size: 10px;
  font-weight: 500;
  text-align: center;
  cursor: pointer;
  transition: color .13s;
  height: 1.5rem;
}

.tilt-panel__apply-all:hover {
  background-color: #F1F5F9;
}
</style>
